"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTranslations } from "next-intl";
import LocaleSwitcher from "@/components/i18n/LocaleSwitcher";
import { useState, useEffect } from "react";

export default function TermsPageClient() {
  const t = useTranslations("Terms");
  const lastUpdated = new Date("2025-05-05").toLocaleDateString();
  const [mailtoLink, setMailtoLink] = useState("contact@taberu.ai");

  // Set mailto link only after component mounts to prevent hydration mismatch
  useEffect(() => {
    setMailtoLink("mailto:contact@taberu.ai");
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6 flex justify-between items-center">
        <Link href={"/" as const}>
          <Button variant="ghost" size="sm" className="gap-2">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {t("back")}
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          <ThemeToggle />
        </div>
      </div>
      <main>
        <Card>
          <CardContent className="p-6 prose dark:prose-invert max-w-none">
            <h1 className="text-3xl font-bold mb-6">{t("title")}</h1>
            <p className="text-sm text-muted-foreground mb-4">
              <time dateTime="2025-05-05">
                {t("lastUpdated")}: {lastUpdated}
              </time>
            </p>

            <section className="mb-8" aria-labelledby="agreement">
              <h2 id="agreement" className="text-2xl font-semibold mb-4">
                {t("sections.agreement.title")}
              </h2>
              <p>{t("sections.agreement.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="description">
              <h2 id="description" className="text-2xl font-semibold mb-4">
                {t("sections.description.title")}
              </h2>
              <p>{t("sections.description.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.description.features.translation")}</li>
                <li>{t("sections.description.features.digitization")}</li>
                <li>{t("sections.description.features.hosting")}</li>
                <li>{t("sections.description.features.qrCode")}</li>
                <li>{t("sections.description.features.analytics")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="accounts">
              <h2 id="accounts" className="text-2xl font-semibold mb-4">
                {t("sections.accounts.title")}
              </h2>
              <p>{t("sections.accounts.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.accounts.responsibilities.security")}</li>
                <li>{t("sections.accounts.responsibilities.activities")}</li>
                <li>{t("sections.accounts.responsibilities.unauthorized")}</li>
                <li>{t("sections.accounts.responsibilities.current")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="fair-use">
              <h2 id="fair-use" className="text-2xl font-semibold mb-4">
                {t("sections.fairUse.title")}
              </h2>

              <section aria-labelledby="credit-system">
                <h3 id="credit-system" className="text-xl font-semibold mb-3">
                  {t("sections.fairUse.creditSystem.title")}
                </h3>
                <p className="mb-4">
                  {t("sections.fairUse.creditSystem.content")}
                </p>
                <ul className="list-disc pl-6 mb-4">
                  <li>
                    <strong>
                      {t("sections.fairUse.creditSystem.tiers.free.title")}
                    </strong>{" "}
                    {t("sections.fairUse.creditSystem.tiers.free.content")}{" "}
                    <span className="text-muted-foreground text-sm">
                      {t("sections.fairUse.creditSystem.tiers.free.note")}
                    </span>
                  </li>
                  <li>
                    <strong>
                      {t("sections.fairUse.creditSystem.tiers.premium.title")}
                    </strong>{" "}
                    {t("sections.fairUse.creditSystem.tiers.premium.content")}
                  </li>
                  <li>
                    <strong>
                      {t("sections.fairUse.creditSystem.tiers.lifetime.title")}
                    </strong>{" "}
                    {t("sections.fairUse.creditSystem.tiers.lifetime.content")}
                  </li>
                  <li>
                    {t(
                      "sections.fairUse.creditSystem.additionalInfo.additional"
                    )}
                  </li>
                  <li>
                    {t("sections.fairUse.creditSystem.additionalInfo.monthly")}
                  </li>
                  <li>
                    {t("sections.fairUse.creditSystem.additionalInfo.each")}
                  </li>
                </ul>
              </section>

              <section aria-labelledby="api-limits">
                <h3 id="api-limits" className="text-xl font-semibold mb-3">
                  {t("sections.fairUse.apiLimits.title")}
                </h3>
                <p className="mb-4">
                  {t("sections.fairUse.apiLimits.content")}
                </p>
                <ul className="list-disc pl-6 mb-4">
                  <li>{t("sections.fairUse.apiLimits.limits.rateLimited")}</li>
                  <li>{t("sections.fairUse.apiLimits.limits.automated")}</li>
                  <li>{t("sections.fairUse.apiLimits.limits.bulk")}</li>
                  <li>{t("sections.fairUse.apiLimits.limits.adjust")}</li>
                </ul>
              </section>

              <section aria-labelledby="ai-restrictions">
                <h3 id="ai-restrictions" className="text-xl font-semibold mb-3">
                  {t("sections.fairUse.aiRestrictions.title")}
                </h3>
                <p className="mb-4">
                  {t("sections.fairUse.aiRestrictions.content")}
                </p>
                <ul className="list-disc pl-6 mb-4">
                  <li>
                    {t("sections.fairUse.aiRestrictions.restrictions.menuOnly")}
                  </li>
                  <li>
                    {t(
                      "sections.fairUse.aiRestrictions.restrictions.manipulation"
                    )}
                  </li>
                  <li>
                    {t("sections.fairUse.aiRestrictions.restrictions.reject")}
                  </li>
                  <li>
                    {t("sections.fairUse.aiRestrictions.restrictions.asIs")}
                  </li>
                </ul>
              </section>
            </section>

            <section className="mb-8" aria-labelledby="intellectual-property">
              <h2
                id="intellectual-property"
                className="text-2xl font-semibold mb-4"
              >
                {t("sections.intellectualProperty.title")}
              </h2>
              <p className="mb-4">
                {t("sections.intellectualProperty.content")}
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.intellectualProperty.license.process")}</li>
                <li>{t("sections.intellectualProperty.license.store")}</li>
                <li>{t("sections.intellectualProperty.license.generate")}</li>
                <li>{t("sections.intellectualProperty.license.anonymized")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="user-content">
              <h2 id="user-content" className="text-2xl font-semibold mb-4">
                {t("sections.userContent.title")}
              </h2>
              <p className="mb-4">{t("sections.userContent.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.userContent.prohibited.infringement")}</li>
                <li>{t("sections.userContent.prohibited.offensive")}</li>
                <li>{t("sections.userContent.prohibited.malicious")}</li>
                <li>{t("sections.userContent.prohibited.laws")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="payment">
              <h2 id="payment" className="text-2xl font-semibold mb-4">
                {t("sections.payment.title")}
              </h2>
              <p className="mb-4">{t("sections.payment.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.payment.terms.stripe")}</li>
                <li>{t("sections.payment.terms.billing")}</li>
                <li>{t("sections.payment.terms.refunds")}</li>
                <li>{t("sections.payment.terms.priceUpdates")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="modifications">
              <h2 id="modifications" className="text-2xl font-semibold mb-4">
                {t("sections.modifications.title")}
              </h2>
              <p>{t("sections.modifications.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="liability">
              <h2 id="liability" className="text-2xl font-semibold mb-4">
                {t("sections.liability.title")}
              </h2>
              <p>{t("sections.liability.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="termination">
              <h2 id="termination" className="text-2xl font-semibold mb-4">
                {t("sections.termination.title")}
              </h2>
              <p>{t("sections.termination.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="governing">
              <h2 id="governing" className="text-2xl font-semibold mb-4">
                {t("sections.governing.title")}
              </h2>
              <p>{t("sections.governing.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="changes">
              <h2 id="changes" className="text-2xl font-semibold mb-4">
                {t("sections.changes.title")}
              </h2>
              <p>{t("sections.changes.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="contact">
              <h2 id="contact" className="text-2xl font-semibold mb-4">
                {t("sections.contact.title")}
              </h2>
              <p>
                {t("sections.contact.content")}{" "}
                <a href={mailtoLink} className="text-primary hover:underline">
                  contact@taberu.ai
                </a>
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
